// Group-assignment dropdown extracted from `DraftBoxItemRow.tsx`
// (#1178 §7 step 5). The same affordance now powers two surfaces:
//   - manager draft-edit path inside `DraftBoxItemRow` (party='logist'),
//   - logist regroup edit-mode inside `RequestBoxesView`.
// Lives in `components/` (not `components/tabs/`) so both consumers
// can import it without crossing a tab boundary.

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX } from '@magimp/shared';
import { Field } from './DraftFormPrimitives.js';

export interface BoxGroupAssignDropdownProps {
  /** Current group ordinal of the item being edited. */
  value: number;
  /** Existing group ordinals on the parent box (deduplicated, ascending). */
  existingGroupOrdinals: ReadonlyArray<number>;
  /**
   * Next ordinal minted when the user picks the "new group" entry.
   * Caller computes `max(existing) + 1` so gaps from removals are
   * preserved (`[0, 2]` → next = 3).
   */
  nextNewGroupOrdinal: number;
  disabled?: boolean;
  /**
   * Optional override for the surrounding `<Field>` label. Defaults to
   * the shared `requests:draft.boxes.groups.action.assign` key so the
   * existing draft-row affordance keeps its copy verbatim.
   */
  label?: string;
  /** `data-testid` for the inner `<select>`. */
  testId?: string;
  /** Visual style override for the select element (consumer-provided). */
  selectStyle?: CSSProperties;
  onChange: (nextOrdinal: number) => void;
}

export function BoxGroupAssignDropdown({
  value,
  existingGroupOrdinals,
  nextNewGroupOrdinal,
  disabled,
  label,
  testId,
  selectStyle,
  onChange,
}: BoxGroupAssignDropdownProps) {
  const { t } = useTranslation(['requests']);
  const canMintNewGroup = nextNewGroupOrdinal <= REQUEST_BOX_ITEM_GROUP_ORDINAL_MAX;
  const options = dedupeAscending([...existingGroupOrdinals, value]);

  function handleRaw(raw: string): void {
    if (raw === '__new__') {
      if (!canMintNewGroup) return;
      onChange(nextNewGroupOrdinal);
      return;
    }
    const next = Number.parseInt(raw, 10);
    if (Number.isInteger(next) && next >= 0) {
      onChange(next);
    }
  }

  return (
    <Field label={label ?? t('requests:draft.boxes.groups.action.assign')}>
      <select
        aria-label={t('requests:draft.boxes.groups.action.assign')}
        title={t('requests:draft.boxes.groups.tooltip.cannotShipTogether')}
        value={String(value)}
        disabled={disabled}
        onChange={(e) => handleRaw(e.target.value)}
        style={selectStyle}
        data-testid={testId}
      >
        {/*
          Include the current ordinal in the options even if it is not
          in `existingGroupOrdinals` (defensive: keeps the select
          controlled while the parent re-renders).
        */}
        {options.map((ord) => (
          <option key={ord} value={String(ord)}>
            {t('requests:draft.boxes.groups.label.group', { ordinal: ord })}
          </option>
        ))}
        {canMintNewGroup && (
          <option value="__new__">{t('requests:draft.boxes.groups.action.newGroup')}</option>
        )}
      </select>
    </Field>
  );
}

function dedupeAscending(values: ReadonlyArray<number>): number[] {
  return [...new Set(values)].sort((a, b) => a - b);
}
